import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Input, Label } from "@/components/ui/input";
import {
  canonicalLocation,
  formatHybridLocation,
  parseHybridLocation,
  searchCities,
  searchLocations,
} from "@/lib/locations";
import { cn } from "@/lib/utils";

type Props = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  noMatchesLabel?: string;
  /** Label for the Hybrid office city field. */
  hybridCityLabel?: string;
  /** Placeholder for the Hybrid office city field. */
  hybridCityPh?: string;
  className?: string;
  id?: string;
  required?: boolean;
};

type SuggestFieldProps = {
  id: string;
  value: string;
  onCommit: (value: string) => void;
  onTyping: (value: string) => void;
  suggestions: string[];
  placeholder?: string;
  noMatchesLabel: string;
  required?: boolean;
};

function SuggestField({
  id,
  value,
  onCommit,
  onTyping,
  suggestions,
  placeholder,
  noMatchesLabel,
  required,
}: SuggestFieldProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState(value);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);

  useEffect(() => {
    setQuery(value);
  }, [value]);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  function pick(opt: string) {
    onCommit(opt);
    setQuery(opt);
    setOpen(false);
  }

  return (
    <div className="relative" ref={rootRef}>
      <Input
        id={id}
        value={query}
        autoComplete="off"
        required={required}
        placeholder={placeholder}
        aria-autocomplete="list"
        aria-expanded={open}
        onChange={(e) => {
          const next = e.target.value;
          setQuery(next);
          setOpen(true);
          setHighlight(0);
          onTyping(next);
        }}
        onFocus={() => {
          setOpen(true);
          setHighlight(0);
        }}
        onBlur={() => {
          window.setTimeout(() => setOpen(false), 120);
        }}
        onKeyDown={(e) => {
          if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
            setOpen(true);
            return;
          }
          if (e.key === "Escape") {
            setOpen(false);
            return;
          }
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlight((i) =>
              suggestions.length ? Math.min(i + 1, suggestions.length - 1) : 0,
            );
            return;
          }
          if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlight((i) => Math.max(i - 1, 0));
            return;
          }
          if (e.key === "Enter" && open && suggestions[highlight]) {
            e.preventDefault();
            pick(suggestions[highlight]);
          }
        }}
      />
      {open && suggestions.length > 0 && (
        <ul
          className="absolute z-20 mt-1 max-h-48 w-full overflow-auto rounded-xl border border-border bg-card py-1 shadow-lg"
          role="listbox"
        >
          {suggestions.map((opt, i) => (
            <li key={opt} role="option" aria-selected={i === highlight}>
              <button
                type="button"
                className={cn(
                  "flex w-full px-3 py-2 text-start text-sm text-foreground",
                  i === highlight ? "bg-surface" : "hover:bg-surface",
                )}
                onMouseEnter={() => setHighlight(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(opt)}
              >
                {opt}
              </button>
            </li>
          ))}
        </ul>
      )}
      {open && query.trim() && suggestions.length === 0 && (
        <div className="absolute z-20 mt-1 w-full rounded-xl border border-border bg-card px-3 py-2 text-sm text-muted shadow-lg">
          {noMatchesLabel}
        </div>
      )}
    </div>
  );
}

export function LocationTypeahead({
  value,
  onChange,
  placeholder,
  noMatchesLabel = "No matching locations",
  hybridCityLabel = "Office location",
  hybridCityPh = "Type a city…",
  className,
  id,
  required,
}: Props) {
  const autoId = useId();
  const inputId = id || autoId;
  const cityId = `${inputId}-hybrid-city`;

  const hybrid = parseHybridLocation(value);
  const isHybrid = hybrid.isHybrid;
  const primaryDisplay = isHybrid ? "Hybrid" : value;
  const cityDisplay = hybrid.city || "";

  // When Hybrid is selected, open the primary list to Remote/Hybrid/cities so they can switch.
  const primarySuggestions = useMemo(
    () =>
      searchLocations(
        isHybrid && /^hybrid$/i.test(primaryDisplay.trim())
          ? ""
          : primaryDisplay,
        8,
      ),
    [isHybrid, primaryDisplay],
  );
  const citySuggestions = useMemo(
    () => searchCities(cityDisplay, 8),
    [cityDisplay],
  );

  function commitPrimary(raw: string) {
    const trimmed = raw.trim();
    if (/^hybrid$/i.test(trimmed)) {
      onChange(hybrid.city ? formatHybridLocation(hybrid.city) : "Hybrid");
      return;
    }
    const exact = canonicalLocation(trimmed);
    onChange(exact || trimmed);
  }

  function commitCity(raw: string) {
    const exact = canonicalLocation(raw.trim());
    if (exact && !parseHybridLocation(exact).isHybrid && exact !== "Remote") {
      onChange(formatHybridLocation(exact));
      return;
    }
    onChange(raw.trim() ? formatHybridLocation(raw.trim()) : "Hybrid");
  }

  return (
    <div className={cn("space-y-2", className)}>
      <SuggestField
        id={inputId}
        value={primaryDisplay}
        suggestions={primarySuggestions}
        placeholder={placeholder}
        noMatchesLabel={noMatchesLabel}
        required={required && !isHybrid}
        onTyping={(next) => {
          if (/^hybrid$/i.test(next.trim())) {
            onChange(
              hybrid.city ? formatHybridLocation(hybrid.city) : "Hybrid",
            );
            return;
          }
          onChange(next);
        }}
        onCommit={commitPrimary}
      />

      {isHybrid && (
        <div>
          <Label
            htmlFor={cityId}
            className="mb-1.5 text-xs font-medium text-muted"
          >
            {hybridCityLabel}
          </Label>
          <SuggestField
            id={cityId}
            value={cityDisplay}
            suggestions={citySuggestions}
            placeholder={hybridCityPh}
            noMatchesLabel={noMatchesLabel}
            required={required}
            onTyping={(next) => {
              onChange(
                next.trim() ? formatHybridLocation(next.trim()) : "Hybrid",
              );
            }}
            onCommit={commitCity}
          />
        </div>
      )}
    </div>
  );
}
